import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PRODUCTS, IMAGES } from "@/data";

const PRODUCT_BADGES = {
  "i-joists": "EACOM P-3 Joist",
  "lvl-lsl": "Roseburg 2.1E LVL",
  "floor-trusses": "Engineered Profile",
};

const CONTAINED_SLUGS = new Set(["i-joists", "floor-trusses"]);

export default function Products() {
  return (
    <div data-testid="products-page" className="pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 mb-16 lg:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <div className="hu-section-tag mb-6">Product Catalog</div>
            <h1 className="font-display font-bold text-5xl lg:text-7xl tracking-tight leading-[0.95] text-neutral-900">
              Engineered wood,<br />engineered for your job.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pt-12">
            <p className="text-neutral-600 leading-relaxed text-base lg:text-lg">
              We manufacture our own roof and floor trusses and stock a full inventory of
              engineered wood products. Every order is designed to your loading conditions,
              meets industry QC standards and is supplied with stamped engineering.
            </p>
          </div>
        </div>

        {/* Product grid */}
        <div className="space-y-px bg-stone-200 border border-stone-200">
          {PRODUCTS.map((p, i) => {
            const Icon = p.icon;
            const reverse = i % 2 === 1;
            return (
              <div
                key={p.slug}
                data-testid={`product-detail-${p.slug}`}
                id={p.slug}
                className={`grid grid-cols-12 gap-0 bg-stone-50`}
              >
                <div
                  className={`col-span-12 lg:col-span-7 p-8 lg:p-14 ${
                    reverse ? "lg:order-2" : ""
                  }`}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <span className="w-12 h-12 grid place-items-center bg-amber-700 text-white">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <span className="hu-section-tag !before:hidden">
                      0{i + 1} / Product
                    </span>
                  </div>
                  <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight leading-tight text-neutral-900">
                    {p.title}
                  </h2>
                  <p className="mt-5 text-neutral-600 leading-relaxed max-w-2xl">{p.summary}</p>

                  <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-2xl">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-neutral-700">
                        <Check size={16} className="text-amber-700 mt-0.5 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/quote"
                    data-testid={`product-${p.slug}-quote-cta`}
                    className="inline-flex items-center gap-2 mt-10 bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium px-5 py-3 rounded-sm transition-colors"
                  >
                    Quote this product <ArrowRight size={14} />
                  </Link>
                </div>

                <div
                  className={`col-span-12 lg:col-span-5 relative min-h-[280px] lg:min-h-[420px] ${
                    p.slug === "floor-trusses" ? "bg-stone-50" : "bg-emerald-950"
                  } ${reverse ? "lg:order-1" : ""}`}
                >
                  <img
                    src={
                      [IMAGES.hero, IMAGES.floorTruss, IMAGES.p3Joists, IMAGES.roseburgLvl, IMAGES.glulam, IMAGES.framing3][i % 6]
                    }
                    alt={p.title}
                    className={`absolute inset-0 w-full h-full ${
                      CONTAINED_SLUGS.has(p.slug)
                        ? "object-contain p-6 lg:p-10"
                        : "object-cover"
                    }`}
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 bg-stone-50 text-neutral-900 px-3 py-1.5 text-[10px] tracking-widest uppercase font-medium border border-stone-200">
                    {PRODUCT_BADGES[p.slug] || "Huron Truss"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
